/**
 * Service Worker Registration
 * Handles PWA service worker registration with update prompts
 */

import { registerSW } from 'virtual:pwa-register';

// Store for managing update state - using a ref object for HMR safety
export const updateStateRef = {
  current: {
    updateCallback: null as (() => void) | null,
    isUpdatePending: false
  }
};

// This will be called by the app to show the update dialog
export const getUpdateState = () => ({
  isUpdatePending: updateStateRef.current.isUpdatePending,
  performUpdate: () => {
    if (updateStateRef.current.updateCallback) {
      updateStateRef.current.updateCallback();
      updateStateRef.current.isUpdatePending = false;
      updateStateRef.current.updateCallback = null;
    }
  },
  dismissUpdate: () => {
    updateStateRef.current.isUpdatePending = false;
    // Don't clear updateCallback so user can still update later
  }
});

// Store listeners for cleanup
let controllerChangeListener: (() => void) | null = null;
let messageListener: ((event: MessageEvent) => void) | null = null;
let updateInterval: NodeJS.Timeout | null = null;

// Cleanup function for HMR
export const cleanupServiceWorker = () => {
  if (controllerChangeListener && navigator.serviceWorker) {
    navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeListener);
  }
  if (messageListener && navigator.serviceWorker) {
    navigator.serviceWorker.removeEventListener('message', messageListener);
  }
  if (updateInterval) {
    clearInterval(updateInterval);
  }
};

// Register service worker with immediate activation
export const registerServiceWorker = () => {
  // NEVER register service worker in development to avoid stale state issues
  if (import.meta.env.DEV) {
    console.log('[SW] Skipping service worker registration in development mode');
    return;
  }
  
  // Clean up any existing listeners first
  cleanupServiceWorker();
  
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log('New service worker available, prompting for update...');
      // Debounce multiple update notifications
      if (!updateStateRef.current.isUpdatePending) {
        updateStateRef.current.isUpdatePending = true;
        updateStateRef.current.updateCallback = updateSW;
        
        // Dispatch custom event for the app to handle
        window.dispatchEvent(new CustomEvent('sw-update-available'));
      }
    },
    onOfflineReady() {
      console.log('Service worker installed, app ready for offline use');
      // You can show a toast notification here
    },
    onRegisteredSW(swScriptUrl, registration) {
      console.log('Service worker registered:', swScriptUrl);
      
      // Check for updates every hour
      updateInterval = setInterval(() => {
        // Only check for updates if online
        if (navigator.onLine) {
          registration?.update().catch((error) => {
            console.warn('Failed to check for SW updates:', error);
          });
        }
      }, 60 * 60 * 1000);
    },
    onRegisterError(error) {
      console.error('Service worker registration error:', error);
    }
  });
  
  // Handle controller change (new SW activated)
  let refreshing = false;
  controllerChangeListener = () => {
    if (!refreshing) {
      refreshing = true;
      console.log('New service worker activated, reloading page...');
      window.location.reload();
    }
  };
  navigator.serviceWorker?.addEventListener('controllerchange', controllerChangeListener);
  
  // Add message handler for SW communication
  messageListener = (event: MessageEvent) => {
    if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
      // Debounce multiple update notifications
      if (!updateStateRef.current.isUpdatePending) {
        updateStateRef.current.isUpdatePending = true;
        updateStateRef.current.updateCallback = updateSW;
        window.dispatchEvent(new CustomEvent('sw-update-available'));
      }
    }
  };
  navigator.serviceWorker?.addEventListener('message', messageListener);
};

// Export for manual update triggering if needed
export const checkForUpdates = async () => {
  const registration = await navigator.serviceWorker?.getRegistration();
  if (registration) {
    try {
      await registration.update();
      console.log('Manually checked for service worker updates');
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }
};